/**
 * Hooks du catalogue de cours (docs/11).
 *
 * Toute la logique de données vit ici, jamais dans le JSX (docs/24 §3).
 *
 * ⚠️ Aucun de ces hooks ne protège quoi que ce soit. `can_edit`, `can_publish`
 * et `can_delete` viennent du **serveur**, qui les recalcule à chaque route :
 * l'interface s'en sert pour cacher ce qui n'est pas disponible, rien de plus
 * (docs/07 §8).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, expectNoContent, unwrap } from "@/shared/api/client";
import type { ApiError } from "@/shared/api/errors";
import type { components } from "@/shared/api/generated";
import { catalogKeys } from "@/shared/api/query-keys";
import type { ContentDocument } from "@/shared/richtext/document";

export type CourseSummary = components["schemas"]["CourseSummaryResponse"];
export type CourseDetail = components["schemas"]["CourseDetailResponse"];
export type LessonSummary = components["schemas"]["LessonSummaryResponse"];
export type LessonDetail = components["schemas"]["LessonDetailResponse"];
export type CourseDomain = components["schemas"]["CourseDomainResponse"];
export type CourseResource = components["schemas"]["ResourceResponse"];
export type CourseSuggestion = components["schemas"]["CourseSuggestionResponse"];

/**
 * En deçà, on n'interroge pas : le serveur rend de toute façon une liste vide
 * (docs/11 §7), et poser la question quand la réponse est connue ferait deux
 * requêtes par mot tapé pour rien. Le seuil est **redit** ici plutôt que
 * déduit : c'est une décision d'écran — ne pas ouvrir une liste sous deux
 * lettres — qui se trouve coïncider avec celle du moteur.
 */
export const MIN_SUGGEST_LENGTH = 3;

/**
 * Filtres du catalogue. L'index signature les rend utilisables comme clé
 * TanStack Query : deux jeux de filtres doivent donner deux caches distincts.
 */
export interface CourseFilters {
  [key: string]: string | undefined;
  q?: string;
  domain_id?: string;
  level?: string;
  language?: string;
}

interface CoursePage {
  data: CourseSummary[];
  meta: components["schemas"]["PageMeta"];
}

/** Les filtres vides sont omis : `?q=` et « pas de recherche » diffèrent. */
function queryParams(filters: CourseFilters, cursor?: string) {
  return {
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.domain_id ? { domain_id: filters.domain_id } : {}),
    ...(filters.level ? { level: filters.level as "beginner" | "intermediate" | "advanced" } : {}),
    ...(filters.language ? { language: filters.language } : {}),
    ...(cursor === undefined ? {} : { cursor }),
  };
}

/**
 * Le référentiel des domaines ne bouge qu'à l'initiative d'un administrateur :
 * le recharger à chaque montage d'écran serait du trafic pour rien.
 */
export function useCourseDomains(): UseQueryResult<CourseDomain[], ApiError> {
  return useQuery({
    queryKey: catalogKeys.domains(),
    queryFn: () => unwrap(api.GET("/api/v1/course-domains", {})),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Catalogue public.
 *
 * `getNextPageParam` rend `undefined` quand le serveur n'envoie pas de curseur
 * — ce qui est **toujours** le cas d'une recherche par pertinence, qui ne se
 * pagine pas (docs/11 §7). TanStack Query désactive alors « charger la suite »
 * de lui-même : l'écran n'a aucune règle à redécider, et ne peut donc pas
 * proposer une page que le serveur ne sait pas servir.
 */
export function useCourseCatalog(
  filters: CourseFilters,
): UseInfiniteQueryResult<InfiniteData<CoursePage>, ApiError> {
  return useInfiniteQuery({
    queryKey: catalogKeys.catalog(filters),
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET("/api/v1/courses", {
          params: { query: { ...queryParams(filters, pageParam), include_total: true } },
        }),
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.meta.next_cursor ?? undefined,
  });
}

/** Les cours de l'enseignant connecté, brouillons et archives compris. */
export function useMyCourses(
  filters: CourseFilters,
): UseInfiniteQueryResult<InfiniteData<CoursePage>, ApiError> {
  return useInfiniteQuery({
    queryKey: catalogKeys.mine(filters),
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET("/api/v1/courses/mine", {
          params: { query: { ...queryParams(filters, pageParam), include_total: true } },
        }),
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.meta.next_cursor ?? undefined,
  });
}

/**
 * Autocomplétion du catalogue (docs/11 §7).
 *
 * Distincte de `useCourseCatalog` et de son `q` : celle-ci cherche **un mot en
 * train d'être écrit**, l'autre **des cours**. Elles n'ont ni le même moteur —
 * trigrammes contre plein texte —, ni le même cache, ni la même durée de vie.
 *
 * `staleTime` est long et `gcTime` aussi : les titres du catalogue ne changent
 * pas pendant une frappe, et retaper une lettre puis l'effacer doit rendre la
 * liste **instantanément**, depuis le cache, plutôt que de la faire clignoter.
 *
 * L'appelant débounce lui-même la valeur qu'il passe : le hook ne sait pas si
 * elle vient d'un champ de saisie ou d'un lien, et poser le minuteur ici le
 * rendrait inutilisable dans le second cas.
 */
export function useCourseSuggestions(text: string): UseQueryResult<CourseSuggestion[], ApiError> {
  const trimmed = text.trim();
  return useQuery({
    queryKey: catalogKeys.suggestions(trimmed),
    enabled: trimmed.length >= MIN_SUGGEST_LENGTH,
    queryFn: () =>
      unwrap(api.GET("/api/v1/courses/suggest", { params: { query: { q: trimmed } } })),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCourse(courseId: string): UseQueryResult<CourseDetail, ApiError> {
  return useQuery({
    queryKey: catalogKeys.course(courseId),
    queryFn: () =>
      unwrap(api.GET("/api/v1/courses/{course_id}", { params: { path: { course_id: courseId } } })),
  });
}

export function useCourseBySlug(slug: string): UseQueryResult<CourseDetail, ApiError> {
  return useQuery({
    queryKey: catalogKeys.courseBySlug(slug),
    queryFn: () =>
      unwrap(api.GET("/api/v1/courses/by-slug/{slug}", { params: { path: { slug } } })),
  });
}

export function useLesson(
  courseId: string,
  lessonId: string | undefined,
): UseQueryResult<LessonDetail, ApiError> {
  return useQuery({
    queryKey: catalogKeys.lesson(courseId, lessonId ?? ""),
    enabled: lessonId !== undefined,
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/courses/{course_id}/lessons/{lesson_id}", {
          params: { path: { course_id: courseId, lesson_id: lessonId ?? "" } },
        }),
      ),
  });
}

/**
 * Invalide la fiche et les listes après toute écriture.
 *
 * Les deux, parce que les deux affichent l'état modifié : publier un cours
 * change sa fiche **et** le fait apparaître au catalogue. N'en rafraîchir
 * qu'une laisse l'autre afficher un statut périmé jusqu'au rechargement.
 */
function useAfterCourseChange() {
  const queryClient = useQueryClient();
  return (courseId?: string) => {
    if (courseId !== undefined) {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.course(courseId) });
    }
    void queryClient.invalidateQueries({ queryKey: catalogKeys.allCourses() });
  };
}

interface CreateCourseInput {
  title: string;
  domain_id: string;
}

/**
 * La langue par défaut du contenu créé.
 *
 * Le contrat la déclare obligatoire ; l'écran ne la demande pas encore. La
 * poser ici, une fois, vaut mieux que de la répandre sur chaque appelant — et
 * le jour où un sélecteur apparaîtra, c'est cette constante qui disparaîtra.
 */
const DEFAULT_COURSE_LANGUAGE = "fr";

export function useCreateCourse(): UseMutationResult<CourseDetail, ApiError, CreateCourseInput> {
  const afterChange = useAfterCourseChange();
  return useMutation({
    mutationFn: (body: CreateCourseInput) =>
      unwrap(api.POST("/api/v1/courses", { body: { ...body, language: DEFAULT_COURSE_LANGUAGE } })),
    onSuccess: (course) => {
      afterChange(course.course.id);
    },
  });
}

export type CourseUpdate = components["schemas"]["UpdateCourseRequest"];

export function useUpdateCourse(
  courseId: string,
): UseMutationResult<CourseDetail, ApiError, CourseUpdate> {
  const afterChange = useAfterCourseChange();
  return useMutation({
    mutationFn: (body: CourseUpdate) =>
      unwrap(
        api.PATCH("/api/v1/courses/{course_id}", {
          params: { path: { course_id: courseId } },
          body,
        }),
      ),
    onSuccess: () => {
      afterChange(courseId);
    },
  });
}

type LifecycleAction = "publish" | "unpublish" | "archive";

/**
 * Un seul hook pour les trois transitions : elles partagent la forme de l'appel
 * et l'invalidation. Trois hooks quasi identiques finiraient par diverger sur
 * ce qu'ils invalident, et c'est celui qu'on oublie qui laisse un écran menteur.
 */
export function useCourseLifecycle(
  courseId: string,
): UseMutationResult<CourseDetail, ApiError, LifecycleAction> {
  const afterChange = useAfterCourseChange();
  return useMutation({
    mutationFn: (action: LifecycleAction) => {
      const path = { params: { path: { course_id: courseId } } };
      if (action === "publish") {
        return unwrap(api.POST("/api/v1/courses/{course_id}/publish", path));
      }
      if (action === "unpublish") {
        return unwrap(api.POST("/api/v1/courses/{course_id}/unpublish", path));
      }
      return unwrap(api.POST("/api/v1/courses/{course_id}/archive", path));
    },
    onSuccess: () => {
      afterChange(courseId);
    },
  });
}

export function useDeleteCourse(): UseMutationResult<void, ApiError, string> {
  const afterChange = useAfterCourseChange();
  return useMutation({
    mutationFn: (courseId: string) =>
      expectNoContent(
        api.DELETE("/api/v1/courses/{course_id}", {
          params: { path: { course_id: courseId } },
        }),
      ),
    onSuccess: () => {
      afterChange();
    },
  });
}

interface CreateLessonInput {
  title: string;
  position?: number;
  /**
   * Accord explicite pour insérer au milieu d'un cours publié (docs/11 §2).
   * Faux par défaut, et c'est le point : le serveur rend 409 tant que
   * l'enseignant n'a pas confirmé qu'il modifie le parcours d'élèves engagés.
   */
  confirm_insertion?: boolean;
}

export function useCreateLesson(
  courseId: string,
): UseMutationResult<LessonDetail, ApiError, CreateLessonInput> {
  const afterChange = useAfterCourseChange();
  return useMutation({
    mutationFn: (body: CreateLessonInput) =>
      unwrap(
        api.POST("/api/v1/courses/{course_id}/lessons", {
          params: { path: { course_id: courseId } },
          body: { ...body, confirm_insertion: body.confirm_insertion ?? false },
        }),
      ),
    onSuccess: () => {
      afterChange(courseId);
    },
  });
}

export type LessonUpdate = components["schemas"]["UpdateLessonRequest"];

export function useUpdateLesson(
  courseId: string,
  lessonId: string,
): UseMutationResult<LessonDetail, ApiError, LessonUpdate> {
  const queryClient = useQueryClient();
  const afterChange = useAfterCourseChange();
  return useMutation({
    mutationFn: (body: LessonUpdate) =>
      unwrap(
        api.PATCH("/api/v1/courses/{course_id}/lessons/{lesson_id}", {
          params: { path: { course_id: courseId, lesson_id: lessonId } },
          body,
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.lesson(courseId, lessonId) });
      afterChange(courseId);
    },
  });
}

export function useDeleteLesson(courseId: string): UseMutationResult<void, ApiError, string> {
  const afterChange = useAfterCourseChange();
  return useMutation({
    mutationFn: (lessonId: string) =>
      expectNoContent(
        api.DELETE("/api/v1/courses/{course_id}/lessons/{lesson_id}", {
          params: { path: { course_id: courseId, lesson_id: lessonId } },
        }),
      ),
    onSuccess: () => {
      afterChange(courseId);
    },
  });
}

/**
 * Réordonnancement : la séquence **complète**, en un appel.
 *
 * L'API refuse une liste partielle (422). C'est délibéré et l'écran doit s'y
 * conformer : envoyer un déplacement isolé laisserait le serveur inventer la
 * place des leçons absentes.
 */
export function useReorderLessons(courseId: string): UseMutationResult<void, ApiError, string[]> {
  const afterChange = useAfterCourseChange();
  return useMutation({
    mutationFn: (lessonIds: string[]) =>
      expectNoContent(
        api.POST("/api/v1/courses/{course_id}/lessons/reorder", {
          params: { path: { course_id: courseId } },
          body: { lesson_ids: lessonIds },
        }),
      ),
    onSuccess: () => {
      afterChange(courseId);
    },
  });
}

interface AttachResourceInput {
  lessonId: string;
  type: "link" | "video" | "pdf" | "image";
  title: string;
  /** Pour `link` et `video`. Absent sur un fichier téléversé. */
  url?: string;
  /** Pour `pdf` et `image` : un fichier déjà déposé **et confirmé**. */
  fileId?: string;
}

export function useSetCourseCover(
  courseId: string,
): UseMutationResult<CourseDetail, ApiError, { fileId: string | null }> {
  // Pas de `queryClient` ici : `useAfterCourseChange` invalide déjà tout ce que
  // la couverture touche — la fiche, la liste de l'enseignant et le catalogue
  // public, où l'image s'affiche. En ajouter un second appel donnerait deux
  // endroits à garder d'accord pour la même écriture.
  const afterChange = useAfterCourseChange();
  return useMutation({
    mutationFn: ({ fileId }: { fileId: string | null }) =>
      unwrap(
        api.PUT("/api/v1/courses/{course_id}/cover", {
          params: { path: { course_id: courseId } },
          // `file_id: null` **retire** la couverture. Le champ est obligatoire
          // côté serveur pour que le retrait soit un geste explicite plutôt
          // qu'un effet de bord de l'omission.
          body: { file_id: fileId },
        }),
      ),
    onSuccess: () => {
      afterChange(courseId);
    },
  });
}

export function useAttachResource(
  courseId: string,
): UseMutationResult<CourseResource, ApiError, AttachResourceInput> {
  const queryClient = useQueryClient();
  const afterChange = useAfterCourseChange();
  return useMutation({
    mutationFn: ({ lessonId, type, title, url, fileId }: AttachResourceInput) =>
      unwrap(
        api.POST("/api/v1/courses/{course_id}/lessons/{lesson_id}/resources", {
          params: { path: { course_id: courseId, lesson_id: lessonId } },
          // `url` **ou** `file_id`, selon le type — jamais les deux. C'est le
          // serveur qui tient l'invariant (`ck_resource_target` de docs/05 §5) ;
          // le redire ici en donnerait une seconde version à garder d'accord.
          body: { type, title, url: url ?? null, file_id: fileId ?? null },
        }),
      ),
    onSuccess: (_data, { lessonId }) => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.lesson(courseId, lessonId) });
      afterChange(courseId);
    },
  });
}

interface DetachResourceInput {
  lessonId: string;
  resourceId: string;
}

export function useDetachResource(
  courseId: string,
): UseMutationResult<void, ApiError, DetachResourceInput> {
  const queryClient = useQueryClient();
  const afterChange = useAfterCourseChange();
  return useMutation({
    mutationFn: ({ lessonId, resourceId }: DetachResourceInput) =>
      expectNoContent(
        api.DELETE("/api/v1/courses/{course_id}/lessons/{lesson_id}/resources/{resource_id}", {
          params: {
            path: { course_id: courseId, lesson_id: lessonId, resource_id: resourceId },
          },
        }),
      ),
    onSuccess: (_data, { lessonId }) => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.lesson(courseId, lessonId) });
      afterChange(courseId);
    },
  });
}

/* ── Exercices ─────────────────────────────────────────────────────────────
 *
 * `Exercise` porte le corrigé et n'arrive que dans `lesson.exercises`, servi au
 * seul auteur ; `ExercisePreview` est la vue élève, et n'a aucun champ où
 * ranger une bonne réponse. Les deux types viennent du serveur — l'écran ne
 * fabrique ni ne filtre l'un depuis l'autre.
 */

export type Exercise = components["schemas"]["ExerciseResponse"];
export type ExerciseQuestion = components["schemas"]["QuestionResponse"];
export type ExercisePreview = components["schemas"]["ExercisePreviewResponse"];
export type QuestionChoice = components["schemas"]["ChoiceResponse"];
export type QuestionType = ExerciseQuestion["type"];
export type ChoiceInput = components["schemas"]["ChoiceRequest"];
export type AnswerKeyInput = components["schemas"]["AnswerKeyRequest"];
export type ExerciseSettings = components["schemas"]["UpdateExerciseRequest"];

interface CreateExerciseInput {
  lessonId: string;
  title: string;
}

export function useCreateExercise(
  courseId: string,
): UseMutationResult<Exercise, ApiError, CreateExerciseInput> {
  const queryClient = useQueryClient();
  const afterChange = useAfterCourseChange();
  return useMutation({
    mutationFn: ({ lessonId, title }: CreateExerciseInput) =>
      unwrap(
        api.POST("/api/v1/courses/{course_id}/lessons/{lesson_id}/exercises", {
          params: { path: { course_id: courseId, lesson_id: lessonId } },
          body: { title },
        }),
      ),
    onSuccess: (_data, { lessonId }) => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.lesson(courseId, lessonId) });
      afterChange(courseId);
    },
  });
}

interface UpdateExerciseInput {
  lessonId: string;
  exerciseId: string;
  settings: ExerciseSettings;
}

export function useUpdateExercise(
  courseId: string,
): UseMutationResult<Exercise, ApiError, UpdateExerciseInput> {
  const queryClient = useQueryClient();
  const afterChange = useAfterCourseChange();
  return useMutation({
    mutationFn: ({ exerciseId, settings }: UpdateExerciseInput) =>
      unwrap(
        api.PATCH("/api/v1/courses/{course_id}/exercises/{exercise_id}", {
          params: { path: { course_id: courseId, exercise_id: exerciseId } },
          body: settings,
        }),
      ),
    onSuccess: (_data, { lessonId, exerciseId }) => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.lesson(courseId, lessonId) });
      void queryClient.invalidateQueries({
        queryKey: catalogKeys.exercisePreview(courseId, exerciseId),
      });
      // Le cours aussi : `is_blocking` et la validité d'un exercice entrent
      // dans `publication_blockers`, affiché sur la fiche.
      afterChange(courseId);
    },
  });
}

interface DeleteExerciseInput {
  lessonId: string;
  exerciseId: string;
}

export function useDeleteExercise(
  courseId: string,
): UseMutationResult<void, ApiError, DeleteExerciseInput> {
  const queryClient = useQueryClient();
  const afterChange = useAfterCourseChange();
  return useMutation({
    mutationFn: ({ exerciseId }: DeleteExerciseInput) =>
      expectNoContent(
        api.DELETE("/api/v1/courses/{course_id}/exercises/{exercise_id}", {
          params: { path: { course_id: courseId, exercise_id: exerciseId } },
        }),
      ),
    onSuccess: (_data, { lessonId }) => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.lesson(courseId, lessonId) });
      afterChange(courseId);
    },
  });
}

interface AddQuestionInput {
  lessonId: string;
  exerciseId: string;
  type: QuestionType;
  prompt: ContentDocument;
  explanation?: ContentDocument | null;
  points?: string;
  choices?: ChoiceInput[];
  answerKeys?: AnswerKeyInput[];
}

export function useAddQuestion(
  courseId: string,
): UseMutationResult<ExerciseQuestion, ApiError, AddQuestionInput> {
  const queryClient = useQueryClient();
  const afterChange = useAfterCourseChange();
  return useMutation({
    mutationFn: ({ exerciseId, type, prompt, explanation, points, choices, answerKeys }) =>
      unwrap(
        api.POST("/api/v1/courses/{course_id}/exercises/{exercise_id}/questions", {
          params: { path: { course_id: courseId, exercise_id: exerciseId } },
          body: {
            type,
            prompt,
            // `points` est toujours envoyé : le schéma le déclare avec une
            // valeur par défaut, donc obligatoire dans le contrat généré. Le
            // rendre facultatif ici ferait diverger le client du contrat.
            points: points ?? "1",
            // Les autres champs absents ne sont pas envoyés : le serveur
            // distingue « pas de corrigé fourni » d'une liste vide, qui serait
            // un refus.
            ...(explanation === undefined ? {} : { explanation }),
            ...(choices === undefined ? {} : { choices }),
            ...(answerKeys === undefined ? {} : { answer_keys: answerKeys }),
          },
        }),
      ),
    onSuccess: (_data, { lessonId, exerciseId }) => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.lesson(courseId, lessonId) });
      void queryClient.invalidateQueries({
        queryKey: catalogKeys.exercisePreview(courseId, exerciseId),
      });
      afterChange(courseId);
    },
  });
}

interface UpdateQuestionInput {
  lessonId: string;
  exerciseId: string;
  questionId: string;
  prompt?: ContentDocument;
  explanation?: ContentDocument | null;
  points?: string;
  choices?: ChoiceInput[];
  answerKeys?: AnswerKeyInput[];
}

export function useUpdateQuestion(
  courseId: string,
): UseMutationResult<ExerciseQuestion, ApiError, UpdateQuestionInput> {
  const queryClient = useQueryClient();
  const afterChange = useAfterCourseChange();
  return useMutation({
    mutationFn: ({ questionId, prompt, explanation, points, choices, answerKeys }) =>
      unwrap(
        api.PATCH("/api/v1/courses/{course_id}/questions/{question_id}", {
          params: { path: { course_id: courseId, question_id: questionId } },
          body: {
            ...(prompt === undefined ? {} : { prompt }),
            ...(explanation === undefined ? {} : { explanation }),
            ...(points === undefined ? {} : { points }),
            ...(choices === undefined ? {} : { choices }),
            ...(answerKeys === undefined ? {} : { answer_keys: answerKeys }),
          },
        }),
      ),
    onSuccess: (_data, { lessonId, exerciseId }) => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.lesson(courseId, lessonId) });
      void queryClient.invalidateQueries({
        queryKey: catalogKeys.exercisePreview(courseId, exerciseId),
      });
      afterChange(courseId);
    },
  });
}

interface DeleteQuestionInput {
  lessonId: string;
  exerciseId: string;
  questionId: string;
}

export function useDeleteQuestion(
  courseId: string,
): UseMutationResult<void, ApiError, DeleteQuestionInput> {
  const queryClient = useQueryClient();
  const afterChange = useAfterCourseChange();
  return useMutation({
    mutationFn: ({ questionId }: DeleteQuestionInput) =>
      expectNoContent(
        api.DELETE("/api/v1/courses/{course_id}/questions/{question_id}", {
          params: { path: { course_id: courseId, question_id: questionId } },
        }),
      ),
    onSuccess: (_data, { lessonId, exerciseId }) => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.lesson(courseId, lessonId) });
      void queryClient.invalidateQueries({
        queryKey: catalogKeys.exercisePreview(courseId, exerciseId),
      });
      afterChange(courseId);
    },
  });
}

interface ReorderQuestionsInput {
  lessonId: string;
  exerciseId: string;
  questionIds: string[];
}

export function useReorderQuestions(
  courseId: string,
): UseMutationResult<Exercise, ApiError, ReorderQuestionsInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ exerciseId, questionIds }: ReorderQuestionsInput) =>
      unwrap(
        api.POST("/api/v1/courses/{course_id}/exercises/{exercise_id}/questions/reorder", {
          params: { path: { course_id: courseId, exercise_id: exerciseId } },
          body: { question_ids: questionIds },
        }),
      ),
    onSuccess: (_data, { lessonId, exerciseId }) => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.lesson(courseId, lessonId) });
      void queryClient.invalidateQueries({
        queryKey: catalogKeys.exercisePreview(courseId, exerciseId),
      });
    },
  });
}

/**
 * Prévisualisation : ce que l'élève recevra.
 *
 * `staleTime: 0` et pas de cache long — le serveur applique `shuffle` à chaque
 * appel, et l'enseignant qui rouvre l'aperçu veut voir un **autre** tirage,
 * pas celui d'il y a dix minutes.
 */
export function useExercisePreview(
  courseId: string,
  exerciseId: string | undefined,
): UseQueryResult<ExercisePreview, ApiError> {
  return useQuery({
    queryKey: catalogKeys.exercisePreview(courseId, exerciseId ?? "none"),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/courses/{course_id}/exercises/{exercise_id}/preview", {
          params: { path: { course_id: courseId, exercise_id: exerciseId as string } },
        }),
      ),
    enabled: exerciseId !== undefined,
    staleTime: 0,
    gcTime: 0,
  });
}
